// usePending — query + mutation hooks for pending-detail, notes, followup,
// final-notice, cancel, update, delete, create.
//
// The list hook lives separately at ./usePendingList.ts. Detail-page hooks
// live here to keep the modal / page bundle navigable from a single import.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  CancelPendingPayload,
  CreatePendingPayload,
  PendingDetailResponse,
  PendingNoteDto,
  PendingSummary,
  SendFinalNoticeResponse,
  SendFollowupPayload,
  SendFollowupResponse,
  UpdatePendingPayload,
  SendIncorrectContactPayload,
  SendIncorrectContactResponse,
} from '../../../types/api';

const NOTES_KEY = 'notes';

/** Cache key for the per-pending notes list (sub-resource). */
function notesKey(encId: string) {
  return [...queryKeys.pending.detail(encId), NOTES_KEY] as const;
}

export function usePendingDetail(encId: string | undefined) {
  return useQuery<PendingDetailResponse>({
    queryKey: queryKeys.pending.detail(encId ?? ''),
    enabled: typeof encId === 'string' && encId.length > 0,
    queryFn: async () => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.get<PendingDetailResponse>(
        endpoints.pending.detail(encId),
      );
      if (!res.data) throw new Error('Pending detail response missing data');
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function usePendingNotes(encId: string | undefined) {
  return useQuery<PendingNoteDto[]>({
    queryKey: notesKey(encId ?? ''),
    enabled: typeof encId === 'string' && encId.length > 0,
    queryFn: async () => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.get<{ notes: PendingNoteDto[] }>(
        endpoints.pending.notes(encId),
      );
      if (!res.data) throw new Error('Pending notes response missing data');
      return res.data.notes;
    },
    staleTime: 0,
  });
}

export function useAddPendingNote(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<PendingNoteDto, unknown, { note: string }>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.post<{ note: PendingNoteDto }>(
        endpoints.pending.notes(encId),
        payload,
      );
      if (!res.data) throw new Error('Pending note insert response missing data');
      return res.data.note;
    },
    onSuccess: async () => {
      if (!encId) return;
      await queryClient.invalidateQueries({ queryKey: notesKey(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
    },
  });
}

export function useSendFollowup(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<SendFollowupResponse, unknown, SendFollowupPayload>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.post<SendFollowupResponse>(
        endpoints.pending.followup(encId),
        payload,
      );
      if (!res.data) throw new Error('Followup response missing data');
      return res.data;
    },
    onSuccess: async () => {
      if (!encId) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
      await queryClient.invalidateQueries({ queryKey: notesKey(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}

export function useSendFinalNotice(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<SendFinalNoticeResponse, unknown, SendFollowupPayload>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.post<SendFinalNoticeResponse>(
        endpoints.pending.finalNotice(encId),
        payload,
      );
      if (!res.data) throw new Error('Final notice response missing data');
      return res.data;
    },
    onSuccess: async () => {
      if (!encId) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
      await queryClient.invalidateQueries({ queryKey: notesKey(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}

export function useSendIncorrectContact(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<SendIncorrectContactResponse, unknown, SendIncorrectContactPayload>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.post<SendIncorrectContactResponse>(
        endpoints.pending.incorrectContact(encId),
        payload,
      );
      if (!res.data) throw new Error('Incorrect-contact response missing data');
      return res.data;
    },
    onSuccess: async () => {
      if (!encId) return;
      // The notice records a note on the pending booking.
      await queryClient.invalidateQueries({ queryKey: notesKey(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
    },
  });
}

export function useCancelPending(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<{ pending_booking: PendingSummary }, unknown, CancelPendingPayload>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.post<{ pending_booking: PendingSummary }>(
        endpoints.pending.cancel(encId),
        payload,
      );
      if (!res.data) throw new Error('Cancel response missing data');
      return res.data;
    },
    onSuccess: async () => {
      if (!encId) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
      await queryClient.invalidateQueries({ queryKey: notesKey(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}

export function useUpdatePending(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<{ pending_booking: PendingSummary }, unknown, UpdatePendingPayload>({
    mutationFn: async (payload) => {
      if (!encId) throw new Error('encId required');
      const res = await apiClient.put<{ pending_booking: PendingSummary }>(
        endpoints.pending.update(encId),
        payload,
      );
      if (!res.data) throw new Error('Update response missing data');
      return res.data;
    },
    onSuccess: async () => {
      if (!encId) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.detail(encId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}

export function useDeletePending(encId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      if (!encId) throw new Error('encId required');
      await apiClient.delete(endpoints.pending.delete(encId));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}

export function useCreatePending() {
  const queryClient = useQueryClient();
  return useMutation<{ pending_booking: PendingSummary }, unknown, CreatePendingPayload>({
    mutationFn: async (payload) => {
      const res = await apiClient.post<{ pending_booking: PendingSummary }>(
        endpoints.pending.create,
        payload,
      );
      if (!res.data) throw new Error('Create pending response missing data');
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
    },
  });
}
